import Image from '@components/ui/Image/Image'

import useMediaPicker from '@lib/hooks/useMediaPicker'
import useOnScreen from '@lib/hooks/useOnScreen'
import useWindowDimensions from '@lib/hooks/useWindowDimensions'
import {
  ImageFragment,
  SvgFragment,
  VideoFragment,
} from '@lib/umbraco/generated/types.generated'
import cn from 'clsx'
import { ImageProps } from 'next/image'
import { CSSProperties, FC, useRef, useState } from 'react'
import useDimensions from 'react-cool-dimensions'
import { MediaPicker } from 'types/mediaPicker'
import ARButtonWrapper from './ARButton/ARButtonWrapper'
import DownloadButton from './DownloadButton/DownloadButton'
import PointDimensions from './PointDimensions/PointDimensions'
import SvgRender from './Renders/SvgRender'
import VideoRender from './Renders/VideoRender'
import styles from './SublimeMedia.module.scss'

type SublimeImageProps = {
  mediaPicker: MediaPicker
  hoverEffect?: boolean
  disableBlur?: boolean
  className?: string
  containerClasses?: string
  arFile?: string
  imageSize?: 640 | 750 | 828 | 1080 | 1200 | 1440 | 1920
  aspectRatio?: CSSProperties['aspectRatio']
  objectFit?: CSSProperties['objectFit']
  objectPosition?: CSSProperties['objectPosition']
  hideCaption?: boolean
  disableShopTheLook?: boolean
}

const SublimeMedia: FC<SublimeImageProps & Partial<ImageProps>> = ({
  mediaPicker,
  hoverEffect,
  disableBlur,
  className,
  containerClasses,
  arFile,
  aspectRatio,
  hideCaption,
  disableShopTheLook,
  ...args
}) => {
  const outerContainer = useRef<HTMLDivElement>(null)
  const media = useMediaPicker(mediaPicker)
  const [loaded, setLoaded] = useState<boolean>(false)
  const inViewport = useOnScreen(outerContainer, loaded)
  const { width } = useWindowDimensions()

  const {
    observe,
    width: imageWidth,
    height: imageHeight,
  } = useDimensions<HTMLDivElement | null>()

  if (mediaPicker.media == null) return null

  return (
    <div
      ref={outerContainer}
      className={cn(
        styles.sublimeImage,
        //  hoverEffect && styles.hoverEffect
      )}
    >
      {/* 
        Custom shop-the-look points container.
      */}
      {!disableShopTheLook &&
        mediaPicker?.media.__typename == 'Image' &&
        mediaPicker.media.shopTheLookJson && (
          <PointDimensions
            outerContainer={outerContainer}
            mediaPicker={mediaPicker}
            width={width}
            height={imageHeight}
            loaded={loaded}
            inViewport={inViewport}
          />
        )}

      {/* Observable container of image / video. */}
      <div ref={observe} className={cn(styles.observer, containerClasses)}>
        {/* 
          Download button (for image only.)
        */}
        {mediaPicker?.enableDownloadButton &&
          mediaPicker?.media.__typename == 'Image' && (
            <DownloadButton
              mediaPicker={mediaPicker}
              className={cn(styles.downloadButton, styles.white, 'bodyText')}
            />
          )}

        {/*
          Render AR Button
        */}
        {arFile && <ARButtonWrapper iosSrc={arFile} />}

        {/*
          Some videos are stored as files instead of videos
        */}
        {media?.__typename == 'File' && media.umbracoExtension == 'mp4' && (
          <VideoRender
            className={className}
            video={media as any}
            priority={args.priority}
            aspectRatio={aspectRatio}
          />
        )}
        {/* 
          Render video.
        */}
        {media?.__typename == 'UmbracoMediaVideo' && (
          <VideoRender
            caption={media.caption}
            className={className}
            video={media as VideoFragment}
            priority={args.priority}
            hideCaption={hideCaption}
            aspectRatio={aspectRatio}
          />
        )}

        {/* 
          Render image.
        */}
        {media?.__typename == 'Image' && (
          <Image
            id={media.id}
            src={media.url}
            rootClassName={className}
            hideCaption={hideCaption}
            priority={args.priority}
            aspectRatio={aspectRatio}
            hoverEffect={hoverEffect}
            overwriteCaptionFormatting={media.overwriteCaptionFormatting}
            quality={90}
            setIsLoaded={setLoaded}
            caption={media.caption}
            sizes={args.sizes}
            style={{
              aspectRatio: aspectRatio,
              objectFit: args.objectFit ? args.objectFit : 'cover',
              objectPosition: args.objectPosition
                ? args.objectPosition
                : getFocalPoint(media),
              ...args.style,
            }}
          />
        )}
        {media?.__typename === 'UmbracoMediaVectorGraphics' && (
          <SvgRender media={media as SvgFragment} className={className} />
        )}
      </div>
    </div>
  )
}

const getFocalPoint = (image: Partial<ImageFragment>): string =>
  image.umbracoFile?.focalPoint
    ? `${image.umbracoFile.focalPoint.left * 100}% ${
        image.umbracoFile.focalPoint.top * 100
      }%`
    : '50% 50%'
export default SublimeMedia
